import { DOM } from "./utils/domActions";
import embedStyles from "./styles/embed.scss";
import {
    InitType,
    DataType,
    CardStyle,
    DataListType,
} from "./types/type";
import { WidgetStyle } from "./types/widgetTypes";
import { TrackingUtil } from "./utils/tracking";

import FloatingCard from "./widget/FloatingCard";
import Banner from "./widget/Banner";
import VerticalItemList from "./widget/VerticalItemList";
import VerticalCardList from "./widget/VerticalCardList";
import HorizontalCardList from "./widget/HorizontalCardList";
import SlideList from "./widget/SlideList";

// Extend Window interface to include custom properties
declare global {
    interface Window {
        TS_STORE_DATA?: InitType;
        TS_CARD_STYLE?: CardStyle;
        TS_CARD_DATA?: DataType;
        TrustShopEmbedScript: typeof TrustShopEmbedScript;
        TrustShopEmbed: TrustShopEmbedScript;
        TrustShopRender: () => Promise<void>;
        widgetStyle?: string;
    }
}

// Interface for widget instances
interface WidgetInstance {
    id: string;
    type: WidgetStyle;
    widget: any; // Will be specific widget interface
    container: HTMLElement;
    shadowRoot: ShadowRoot;
}

const CACHE_KEY = 'trustshop_api_cache';

const getCachedData = (): DataType[] | DataListType | null => {
    if (!TrackingUtil.isSessionValid()) return null;
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const setCachedData = (data: DataType[] | DataListType): void => {
    try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {}
};

const getData = async (): Promise<DataType[] | DataListType> => {
    // Return cached data if session is still valid
    const cached = getCachedData();
    if (cached) return cached;

    // Start a new session
    TrackingUtil.ensureSession();

    // Get shop_id, api_url and locale from window.TS_STORE_DATA or use defaults
    const shopId = window.TS_STORE_DATA?.store_id || 73174876382;
    const apiUrl = window.TS_STORE_DATA?.api_url || 'https://ops.trustshop.io';
    const language = window.TS_STORE_DATA?.locale || 'en';
    try {
        const response = await fetch(`${apiUrl}/api/campaigns?shop_id=${shopId}&language=${language}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
            mode: 'cors'
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const responseText = await response.text();

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (parseError) {
            console.error('JSON parse error:', parseError);
            return [];
        }

        // API returns { success: boolean, data: array }
        if (result.success && result.data) {
            setCachedData(result.data);
            return result.data;
        }

        return [];

    } catch (error) {
        console.error('Failed to fetch campaign data:', error);
        return [];
    }
};

class TrustShopEmbedScript {
    isInitialized: boolean = false;
    isLoaded: boolean = false;
    storeData: InitType | null = null;
    private widgetInstances: Map<string, WidgetInstance> = new Map();
    private idCounter: number = 0;

    constructor() {}

    init(): void {
        if (window.TS_STORE_DATA) {
            this.storeData = window.TS_STORE_DATA;
        }
        this.isInitialized = true;
    }

    /**
     * Check if a campaign has been dismissed by the user
     */
    private isCampaignDismissed(data: DataType): boolean {
        if (!data.id) return false;

        const dismissedAt = localStorage.getItem(
            `trustshop_card_${data.id}_dismissed`,
        );
        if (!dismissedAt) return false;

        const interval = data.reappear_interval;
        if (!interval) return true;

        const elapsed = Date.now() - parseInt(dismissedAt, 10);
        if (interval === "after_1_day") return elapsed < 24 * 60 * 60 * 1000;
        if (interval === "after_7_days") return elapsed < 7 * 24 * 60 * 60 * 1000;

        return true;
    }

    /**
     * Generate unique ID for widget instance
     */
    private generateWidgetId(type: WidgetStyle): string {
        this.idCounter++;
        return `widget_${type}_${this.idCounter}_${Date.now()}`;
    }

    /**
     * Create widget container with shadow DOM
     */
    private createWidgetContainer(
        id: string,
        type: WidgetStyle
    ): { container: HTMLElement; shadowRoot: ShadowRoot } {
        const container = DOM.createElement("div", {
            id: `trustshop-embed--${id}`,
            className: `embed-script-container widget-${type}`,
        });

        const shadowRoot = container.attachShadow({ mode: "open" });

        // Inject styles into shadow DOM
        const styleElement = document.createElement("style");
        styleElement.textContent = embedStyles;
        shadowRoot.appendChild(styleElement);

        return { container, shadowRoot };
    }

    /**
     * Create widget instance based on type
     */
    private createWidgetInstance(data: DataType | DataListType): WidgetInstance | null {
        // Ensure we have a type
        if (!data.type) {
            console.warn("Widget data missing type property");
            return null;
        }
        
        const widgetId = this.generateWidgetId(data.type);
        const { container, shadowRoot } = this.createWidgetContainer(
            widgetId,
            data.type
        );

        let widget: any = null;

        switch (data.type) {
            case WidgetStyle.FLOATING_CARD:
                // Create new instance or use singleton
                // For now using singleton, but you can change to new instance
                FloatingCard.updateData(data);
                widget = FloatingCard;
                break;

            case WidgetStyle.BANNER:
                Banner.updateData(data);
                widget = Banner;
                break;

            case WidgetStyle.HORIZONTAL_CARD_LIST:
                HorizontalCardList.updateData(data);
                widget = HorizontalCardList;
                break;

            case WidgetStyle.VERTICAL_CARD_LIST:
                VerticalCardList.updateData(data);
                widget = VerticalCardList;
                break;

            case WidgetStyle.SLIDE_LIST:
                SlideList.updateData(data);
                widget = SlideList;
                break;

            case WidgetStyle.VERTICAL_ITEM_LIST:
                VerticalItemList.updateData(data);
                widget = VerticalItemList;
                break;

            default:
                console.warn(`Unknown widget type: ${data.type}`);
                break;
        }
        if (widget) {
            return {
                id: widgetId,
                type: data.type,
                widget,
                container,
                shadowRoot,
            };
        }

        return null;
    }

    /**
     * Render single widget
     */
    private renderWidget(instance: WidgetInstance): void {
        // Render widget into shadow DOM
        const widgetElement = instance.widget.render();
        instance.shadowRoot.appendChild(widgetElement);
        // Handle positioning only for floating cards
        if (
            instance.type === "floating-card" &&
            instance.widget.handlePosition
        ) {
            instance.widget.handlePosition(
                instance.container,
                window.TS_CARD_STYLE?.position || "left-bottom"
            );
        }
        // Append to body
        document.body.appendChild(instance.container);
    }

    /**
     * Main render method - handles all widgets
     */
    async render(): Promise<void> {
        this.init();
        if (this.isLoaded) return;
        this.isLoaded = true;
        const response = await getData();
        // Only render widgets if we have valid data from API
        if (!response || (Array.isArray(response) && response.length === 0)) {
            console.log('No widget data available from API');
            return;
        }

        // Check if response is DataListType or DataType[]
        if (Array.isArray(response)) {
            // Group by type, sorted by priority descending
            const groupedByType = new Map<string, DataType[]>();
            response.forEach((data) => {
                if (!data.type) return;
                const group = groupedByType.get(data.type) || [];
                group.push(data);
                groupedByType.set(data.type, group);
            });

            // For each type, sort by priority desc and render first non-dismissed item
            groupedByType.forEach((items) => {
                items.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

                for (const data of items) {
                    // Skip dismissed items
                    if (this.isCampaignDismissed(data)) continue;

                    const instance = this.createWidgetInstance(data);
                    if (instance) {
                        this.widgetInstances.set(instance.id, instance);
                        this.renderWidget(instance);
                        break; // Only render one per type
                    }
                }
            });
        } else {
            // It's a DataListType - pass it directly as a single widget
            const instance = this.createWidgetInstance(response as any);
            if (instance) {
                this.widgetInstances.set(instance.id, instance);
                this.renderWidget(instance);
            }
        }
    }

    /**
     * Get specific widget instance by ID
     */
    getWidget(id: string): WidgetInstance | undefined {
        return this.widgetInstances.get(id);
    }

    /**
     * Get all widgets of specific type
     */
    getWidgetsByType(type: WidgetStyle): WidgetInstance[] {
        return Array.from(this.widgetInstances.values()).filter(
            (instance) => instance.type === type
        );
    }

    /**
     * Destroy specific widget
     */
    destroyWidget(id: string): void {
        const instance = this.widgetInstances.get(id);
        if (instance) {
            // Call widget's destroy method
            instance.widget.destroy?.();

            // Remove container
            instance.container.remove();

            // Remove from map
            this.widgetInstances.delete(id);
        }
    }

    /**
     * Destroy all widgets
     */
    destroy(): void {
        // Destroy all widget instances
        this.widgetInstances.forEach((_instance, id) => {
            this.destroyWidget(id);
        });

        // Clear the map
        this.widgetInstances.clear();
        this.isLoaded = false;
    }

    /**
     * Reload all widgets
     */
    reload(): void {
        this.destroy();
        this.render();
    }

    /**
     * Get all active widget instances
     */
    getAllWidgets(): WidgetInstance[] {
        return Array.from(this.widgetInstances.values());
    }

    /**
     * Check if widget exists
     */
    hasWidget(id: string): boolean {
        return this.widgetInstances.has(id);
    }
}

(() => {
    window.TrustShopEmbedScript = TrustShopEmbedScript;
    
    // Create global instance for easy access
    window.TrustShopEmbed = new TrustShopEmbedScript();
    
    // Expose render function globally for manual initialization
    window.TrustShopRender = () => {
        return window.TrustShopEmbed.render();
    };
})();
